import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from './AppIcon';
import LumivetaLogo from './LumivetaLogo';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import api from '../services/api';

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin, isPremium, isTrialing } = useAuth();
  const { language, toggleLanguage, t } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);

  const isActive = (path) => location?.pathname === path;

  // Dynamic navigation - show "Upgrade" for trial, "Premium" for paid
  const navigationItems = [
    { path: '/main-dashboard', label: t('dashboard'), icon: 'LayoutDashboard' },
    { path: '/history', label: t('myGoldMine'), icon: 'Gem' },
    { path: '/account-settings', label: t('account'), icon: 'Settings' },
  ];
  
  // Handle Upgrade - goes straight to checkout
  const handleUpgrade = async () => {
    setUpgradeLoading(true);
    try {
      const result = await api.smartUpgrade(language);
      if (result.url) {
        window.location.href = result.url;
      }
    } catch (error) {
      console.error('Error with upgrade:', error);
      alert(language === 'es' ? 'No se puede procesar. Intenta de nuevo.' : 'Unable to process. Please try again.');
    } finally {
      setUpgradeLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const data = await api.createPortalSession();
      if (data.portal_url) {
        window.location.href = data.portal_url;
      }
    } catch (error) {
      console.error('Error opening subscription portal:', error);
      alert(error.message || (language === 'es' ? 'No se puede abrir la gestión de suscripción. Intenta de nuevo.' : 'Unable to open subscription management. Please try again.'));
    } finally {
      setPortalLoading(false);
    }
  };

  const usage = user?.usage || { used: 0, limit: 5, remaining: 5 };

  return (
    <header 
      className="fixed top-0 left-0 right-0 z-50 glass border-b border-border"
      style={{ paddingTop: 'env(safe-area-inset-top)', marginTop: '0' }}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16" style={{ minHeight: '64px' }}>
          <div className="flex items-center gap-8">
            <Link to="/main-dashboard" className="transition-transform duration-250 hover:scale-105">
              <LumivetaLogo size="small" />
            </Link>

            <nav className="hidden lg:flex items-center gap-1">
              {navigationItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-250 ${
                    isActive(item?.path)
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={item?.icon} size={18} />
                  <span className="font-medium text-sm">{item?.label}</span>
                </Link>
              ))}
              {/* Upgrade button - goes straight to checkout for non-premium users */}
              {!isPremium && !isAdmin && (
                <button
                  onClick={handleUpgrade}
                  disabled={upgradeLoading}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-250 text-primary hover:text-foreground hover:bg-muted"
                  data-testid="header-upgrade-btn"
                >
                  <Icon name="Crown" size={18} color="#FFD700" />
                  <span className="font-medium text-sm">
                    {upgradeLoading ? '...' : t('upgrade')}
                  </span>
                </button>
              )}
            </nav>
          </div>

          <div className="flex items-center gap-3">
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg glass border border-border hover:border-primary/50 transition-all"
              title={t('language')}
              data-testid="language-toggle"
            >
              <Icon name="Globe" size={16} color="#FFD700" />
              <span className="text-xs font-medium text-foreground">
                {language === 'en' ? 'EN' : 'ES'}
              </span>
            </button>

            {user && (
              <div className="hidden lg:flex items-center gap-3 px-4 py-2 rounded-lg glass">
                {isPremium ? (
                  <div className="flex items-center gap-2">
                    <Icon name="Crown" size={16} color="#FFD700" />
                    <span className="text-sm font-caption text-primary">{t('premium')}</span>
                  </div>
                ) : isTrialing ? (
                  <div className="flex items-center gap-2">
                    <Icon name="Sparkles" size={16} color="#FFD700" />
                    <span className="text-xs font-caption text-muted-foreground">
                      {usage.used}/{usage.limit} {user?.usage?.limit_type === 'total' ? (language === 'es' ? 'usadas' : 'used') : t('today')}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Icon name="Sparkles" size={16} color="#FFD700" />
                    <span className="text-xs font-caption text-muted-foreground">
                      {usage.remaining}/{usage.limit} {t('today')}
                    </span>
                  </div>
                )}
              </div>
            )}

            <div className="relative hidden lg:block">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-muted transition-colors duration-250"
              >
                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center border border-primary/30">
                  <Icon name="User" size={16} color="#FFD700" />
                </div>
                <Icon name="ChevronDown" size={16} color="#94A3B8" />
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-56 glass border border-border rounded-xl shadow-lg overflow-hidden">
                  <div className="px-4 py-3 border-b border-border">
                    <p className="font-medium text-foreground truncate">{user?.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                  </div>
                  <Link
                    to="/account-settings"
                    className="flex items-center gap-2 px-4 py-3 hover:bg-muted transition-colors duration-250"
                    onClick={() => setUserMenuOpen(false)}
                  >
                    <Icon name="Settings" size={16} />
                    <span className="text-sm">{t('settings')}</span>
                  </Link>
                  {/* Manage Subscription - for premium users */}
                  {isPremium && (
                    <>
                      <Link
                        to="/subscription-plans?change_plan=true"
                        className="w-full flex items-center gap-2 px-4 py-3 hover:bg-muted transition-colors duration-250 text-left"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <Icon name="RefreshCw" size={16} color="#FFD700" />
                        <span className="text-sm">{language === 'es' ? 'Cambiar Plan' : 'Change Plan'}</span>
                      </Link>
                      <button
                        onClick={() => {
                          setUserMenuOpen(false);
                          handleManageSubscription();
                        }}
                        disabled={portalLoading}
                        className="w-full flex items-center gap-2 px-4 py-3 hover:bg-muted transition-colors duration-250 text-left text-primary"
                      >
                        <Icon name="CreditCard" size={16} color="#FFD700" />
                        <span className="text-sm">{portalLoading ? t('loading') : t('manageSubscription')}</span>
                      </button>
                    </>
                  )}
                  {/* Admin Management - visible for ALL admins */}
                  {isAdmin && (
                    <Link
                      to="/admin-management"
                      className="flex items-center gap-2 px-4 py-3 hover:bg-muted transition-colors duration-250 text-primary"
                      onClick={() => setUserMenuOpen(false)}
                      data-testid="admin-panel-link"
                    >
                      <Icon name="Shield" size={16} color="#FFD700" />
                      <span className="text-sm">{t('adminManagement')}</span>
                    </Link>
                  )}
                  <button
                    onClick={() => {
                      setUserMenuOpen(false);
                      handleLogout();
                    }}
                    className="w-full flex items-center gap-2 px-4 py-3 hover:bg-muted transition-colors duration-250 text-left border-t border-border"
                  >
                    <Icon name="LogOut" size={16} />
                    <span className="text-sm">{t('signOut')}</span>
                  </button>
                </div>
              )}
            </div>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-muted transition-colors duration-250"
            >
              <Icon name={mobileMenuOpen ? 'X' : 'Menu'} size={24} color="#F8FAFC" />
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-border">
            <nav className="flex flex-col gap-2">
              {navigationItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-250 ${
                    isActive(item?.path)
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={item?.icon} size={20} />
                  <span className="font-medium">{item?.label}</span>
                </Link>
              ))}
              {/* Upgrade button - goes straight to checkout (mobile) */}
              {!isPremium && !isAdmin && (
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    handleUpgrade();
                  }}
                  disabled={upgradeLoading}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-250 text-primary hover:bg-muted"
                >
                  <Icon name="Crown" size={20} color="#FFD700" />
                  <span className="font-medium">
                    {upgradeLoading ? '...' : t('upgrade')}
                  </span>
                </button>
              )}
            </nav>

            {user && (
              <div className="mt-4 pt-4 border-t border-border">
                <div className="flex items-center justify-between px-4 py-2 glass rounded-lg mb-2">
                  {isPremium ? (
                    <div className="flex items-center gap-2">
                      <Icon name="Crown" size={16} color="#FFD700" />
                      <span className="text-sm font-caption text-primary">{t('premium')}</span>
                    </div>
                  ) : isTrialing ? (
                    <span className="text-sm font-caption text-muted-foreground">
                      {usage.used}/{usage.limit} {user?.usage?.limit_type === 'total' ? (language === 'es' ? 'usadas' : 'used') : t('today')}
                    </span>
                  ) : (
                    <span className="text-sm font-caption text-muted-foreground">
                      {usage.remaining}/{usage.limit} {t('today')}
                    </span>
                  )}
                </div>

                {/* Manage Subscription - for premium users (mobile) */}
                {isPremium && (
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      handleManageSubscription();
                    }}
                    disabled={portalLoading}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-primary hover:bg-muted transition-all duration-250"
                  >
                    <Icon name="CreditCard" size={20} color="#FFD700" />
                    <span className="font-medium">{portalLoading ? t('loading') : t('manageSubscription')}</span>
                  </button>
                )}

                {/* Admin Management - visible for ALL admins (mobile) */}
                {isAdmin && (
                  <Link
                    to="/admin-management"
                    onClick={() => setMobileMenuOpen(false)}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-primary hover:bg-muted transition-all duration-250"
                    data-testid="admin-panel-link-mobile"
                  >
                    <Icon name="Shield" size={20} color="#FFD700" />
                    <span className="font-medium">{t('adminManagement')}</span>
                  </Link>
                )}

                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    handleLogout();
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-250"
                >
                  <Icon name="LogOut" size={20} />
                  <span className="font-medium">{t('signOut')}</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
